import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AddressAutocomplete from '../components/common/AddressAutocomplete';
import Header from '../components/layout/Header';
import Modal from '../components/common/Modal';
import { getCareProviders, createCareProvider, updateCareProvider, deleteCareProvider, getCustomLists } from '../utils/api';
import { Plus, Search, Building2, Trash2, Phone, Mail, MapPin, Tag } from 'lucide-react';
import toast from 'react-hot-toast';

const DEFAULT_TYPES = ['Doctor','Imaging Center','Pain Management','Pharmacy','Physical Therapy','Chiropractor','Orthopedic','Neurologist','Other'];

const emptyForm = {
  name: '', provider_type: '', specialty: '', phone: '', fax: '',
  email: '', address: '', city: '', state: '', zip: '', notes: '',
  is_active: true, services: [],
};

// ── Reusable toggle switch ────────────────────────────────────────────────────
function Toggle({ checked, onChange, size = 'md' }) {
  const w = size === 'sm' ? 32 : 42;
  const h = size === 'sm' ? 18 : 24;
  const r = size === 'sm' ? 14 : 20;
  const pad = 2;
  return (
    <div
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      title={checked ? 'Active — click to deactivate' : 'Inactive — click to activate'}
      style={{
        width: w, height: h, borderRadius: h, position: 'relative',
        cursor: 'pointer', flexShrink: 0, transition: 'background 0.2s',
        background: checked ? '#22c55e' : 'var(--zeus-border)',
      }}
    >
      <div style={{
        position: 'absolute', top: pad, borderRadius: '50%',
        width: r, height: r, background: '#fff',
        boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
        transition: 'left 0.2s',
        left: checked ? w - r - pad : pad,
      }} />
    </div>
  );
}

export default function NetworkPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.access_level === 'super_admin'
    || user?.role === 'super_admin'
    || user?.role === 'owner';
  const [items, setItems]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [providerTypes, setProviderTypes]     = useState(DEFAULT_TYPES);
  const [specialties,   setSpecialties]       = useState([]);
  const [providerServices, setProviderServices] = useState({}); // { "Doctor": ["X-Ray",...] }
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PER_PAGE = 25;
  const sentinelRef = useRef(null);

  const [q, setQ]                       = useState('');
  const [typeF, setTypeF]               = useState('');
  const [activeF, setActiveF]           = useState('all');
  const [open, setOpen]                 = useState(false);
  const [saving, setSaving]             = useState(false);
  const [editing, setEditing]           = useState(null);
  const [form, setForm]                 = useState({ ...emptyForm });

  const filtered = items;

  useEffect(() => { loadCustomLists(); load(1); }, []);

  useEffect(() => { setItems([]); setPage(1); load(1); }, [typeF, activeF]);

  useEffect(() => {
    const t = setTimeout(() => { setItems([]); setPage(1); load(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loading && !loadingMore && page < totalPages) {
        load(page + 1, true);
      }
    }, { rootMargin: '200px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [page, totalPages, loading, loadingMore, typeF, activeF, q]);

  const loadCustomLists = async () => {
    try {
      const lists = await getCustomLists();
      if (lists?.provider_type_options?.length) setProviderTypes(lists.provider_type_options);
      if (lists?.specialty_options?.length)      setSpecialties(lists.specialty_options);
      if (lists?.provider_services) setProviderServices(lists.provider_services);
    } catch {}
  };

  const load = async (pg = 1, append = false) => {
    try {
      if (append) setLoadingMore(true); else setLoading(true);
      const params = { page: pg, per_page: PER_PAGE };
      if (typeF)   params.type   = typeF;
      if (activeF !== 'all') params.active = activeF === 'active' ? 'true' : 'false';
      if (q.trim()) params.search = q.trim();
      const res = await getCareProviders(params);
      const data = Array.isArray(res) ? res : (res?.providers || []);
      if (append) {
        setItems(prev => [...prev, ...data]);
      } else {
        setItems(data);
      }
      setTotal(res?.total        || data.length);
      setTotalPages(res?.total_pages || 1);
      setPage(pg);
    } catch (e) {
      toast.error(e?.message || 'Failed to load providers');
      if (!append) setItems([]);
    } finally { setLoading(false); setLoadingMore(false); }
  };

  const location = useLocation();

  // Auto-open provider drawer when navigated from global search
  useEffect(() => {
    const pid = location?.state?.openProviderId;
    if (!pid || !items.length) return;
    const found = items.find(p => p.id === pid || p._id === pid);
    if (found) openEdit(found);
  }, [location?.state?.openProviderId, items]); // eslint-disable-line

  const openNew = () => { setEditing(null); setForm({ ...emptyForm }); setOpen(true); };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || '', provider_type: p.provider_type || '', specialty: p.specialty || '',
      phone: p.phone || '', fax: p.fax || '', email: p.email || '',
      address: p.address || '', city: p.city || '', state: p.state || '',
      zip: p.zip || '', notes: p.notes || '', is_active: p.is_active !== false,
      services: Array.isArray(p.services) ? p.services : [],
    });
    setOpen(true);
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Provider name is required'); return; }
    setSaving(true);
    try {
      if (editing?._id) {
        const updated = await updateCareProvider(editing._id, form);
        setItems(prev => prev.map(x => x._id === editing._id ? updated : x));
        toast.success('Provider updated');
      } else {
        const created = await createCareProvider(form);
        setItems(prev => [created, ...prev]);
        toast.success('Provider added');
      }
      setOpen(false);
    } catch (e2) { toast.error(e2?.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const [deleteTarget, setDeleteTarget]   = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting]           = useState(false);

  const onDelete = (p) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admins can delete providers.');
      return;
    }
    setDeleteTarget(p);
    setDeleteConfirm('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirm.trim().toLowerCase() !== deleteTarget.name.toLowerCase()) {
      toast.error('Name does not match — type the provider name exactly.');
      return;
    }
    setDeleting(true);
    try {
      await deleteCareProvider(deleteTarget._id, deleteConfirm.trim());
      setItems(prev => prev.filter(x => x._id !== deleteTarget._id));
      toast.success('Provider deleted');
      setDeleteTarget(null);
    } catch (e) { toast.error(e?.message || 'Delete failed'); }
    finally { setDeleting(false); }
  };

  // Toggle active/inactive directly on the row — no modal needed
  const toggleActive = async (p) => {
    const newVal = p.is_active === false ? true : false;
    setItems(prev => prev.map(x => x._id === p._id ? { ...x, is_active: newVal } : x));
    try {
      await updateCareProvider(p._id, { ...p, is_active: newVal });
      toast.success(newVal ? `${p.name} set to Active` : `${p.name} set to Inactive`);
    } catch (e) {
      setItems(prev => prev.map(x => x._id === p._id ? { ...x, is_active: p.is_active } : x));
      toast.error('Failed to update status');
    }
  };

  return (
    <>
      <Header title="Provider Network">
        <button className="btn btn-primary" onClick={openNew}>
          <Plus size={16} /><span>Add Provider</span>
        </button>
      </Header>

      <div className="app-content">
        {/* ── Toolbar ── */}
        <div className="network-toolbar">
          <div className="search-bar network-search">
            <Search />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search providers..." />
          </div>
          <select className="form-select network-type-filter" value={typeF} onChange={(e) => setTypeF(e.target.value)}>
            <option value="">All Types</option>
            {providerTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          {/* Active/Inactive filter */}
          <select className="form-select" style={{ width: 'auto', minWidth: 110 }} value={activeF} onChange={e => setActiveF(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <span className="network-count">
            {total.toLocaleString()} provider{total !== 1 ? 's' : ''}
          </span>
        </div>

        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 10 }}>
          Double-click any row to edit · Click the toggle to change Active / Inactive status
        </p>

        {loading ? (
          <div className="loading-spinner"><div className="spinner" /></div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <Building2 />
            <h3>No providers found</h3>
            <p>Add doctors, imaging centers, pharmacies, and more.</p>
            <button className="btn btn-primary" onClick={openNew} style={{ marginTop: '16px' }}>
              <Plus size={16} /> Add Provider
            </button>
          </div>
        ) : (
          <>
            {/* ── Desktop table ── */}
            <div className="table-container network-table-view">
              <table>
                <thead>
                  <tr>
                    <th>Name</th><th>Type</th><th>Specialty</th>
                    <th>Phone</th><th>Email</th><th>City</th>
                    <th style={{ width: 100, textAlign: 'center' }}>Status</th>
                    <th style={{ width: 60 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(p => (
                    <tr
                      key={p._id}
                      onDoubleClick={() => openEdit(p)}
                      style={{ cursor: 'pointer', opacity: p.is_active === false ? 0.55 : 1 }}
                      title="Double-click to edit"
                    >
                      <td style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{p.name}</td>
                      <td>{p.provider_type || '—'}</td>
                      <td>{p.specialty || '—'}</td>
                      <td>{p.phone || '—'}</td>
                      <td>{p.email || '—'}</td>
                      <td>{p.city ? `${p.city}${p.state ? `, ${p.state}` : ''}` : '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                          <Toggle checked={p.is_active !== false} onChange={() => toggleActive(p)} size="sm" />
                          <span style={{
                            fontSize: '0.72rem', fontWeight: 600,
                            color: p.is_active !== false ? '#22c55e' : 'var(--text-muted)',
                          }}>
                            {p.is_active !== false ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={(e) => { e.stopPropagation(); onDelete(p); }}
                          title="Delete provider"
                          style={{ color: '#ef4444' }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* ── Mobile cards ── */}
            <div className="network-cards-view">
              {items.map(p => (
                <div
                  key={p._id}
                  className="network-card"
                  style={{ opacity: p.is_active === false ? 0.65 : 1 }}
                  onDoubleClick={() => openEdit(p)}
                >
                  <div className="network-card-header">
                    <div className="network-card-icon"><Building2 size={18} /></div>
                    <div className="network-card-info">
                      <div className="network-card-name">{p.name}</div>
                      {p.provider_type && <span className="network-card-type">{p.provider_type}</span>}
                    </div>
                    <div className="network-card-actions" style={{ gap: 8 }}>
                      <Toggle checked={p.is_active !== false} onChange={() => toggleActive(p)} size="sm" />
                      <button className="btn btn-ghost btn-sm" onClick={(e) => { e.stopPropagation(); onDelete(p); }} style={{ color: '#ef4444' }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="network-card-body">
                    {p.specialty && <div className="network-card-row"><Tag size={13} /><span>{p.specialty}</span></div>}
                    {p.phone && <div className="network-card-row"><Phone size={13} /><a href={`tel:${p.phone}`} onClick={e => e.stopPropagation()}>{p.phone}</a></div>}
                    {p.email && <div className="network-card-row"><Mail size={13} /><a href={`mailto:${p.email}`} onClick={e => e.stopPropagation()}>{p.email}</a></div>}
                    {(p.city || p.address) && <div className="network-card-row"><MapPin size={13} /><span>{[p.address, p.city, p.state].filter(Boolean).join(', ')}</span></div>}
                  </div>
                  <div style={{ padding: '6px 14px 10px', borderTop: '1px solid var(--zeus-border)', display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: '0.78rem' }} onClick={() => openEdit(p)}>
                      Edit Provider
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div ref={sentinelRef} style={{ padding: '16px', textAlign: 'center' }}>
              {loadingMore && <div className="spinner" style={{ margin: '0 auto' }} />}
              {!loadingMore && page < totalPages && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Scroll for more...</span>
              )}
              {!loadingMore && page >= totalPages && items.length > 0 && (
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  Showing all {total.toLocaleString()} provider{total !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Delete Confirm Modal ── */}
      <Modal isOpen={!!deleteTarget} onClose={() => { setDeleteTarget(null); setDeleteConfirm(''); }} title="Delete Provider">
        <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:16 }}>
          <div style={{ padding:'12px', background:'var(--zeus-navy-light)', borderRadius:'var(--radius-md)', border:'1px solid var(--zeus-border)' }}>
            <div style={{ fontWeight:700, fontSize:'0.92rem', color:'var(--text-primary)' }}>{deleteTarget?.name}</div>
            <div style={{ fontSize:'0.82rem', color:'var(--text-muted)', marginTop:2 }}>{deleteTarget?.provider_type}{deleteTarget?.specialty ? ` · ${deleteTarget.specialty}` : ''}</div>
          </div>
          <p style={{ fontSize:'0.85rem', color:'var(--status-red)', fontWeight:600 }}>
             This permanently removes the provider. This cannot be undone.
          </p>
          <div>
            <label style={{ fontSize:'0.82rem', color:'var(--text-muted)', display:'block', marginBottom:4 }}>
              Type the provider name to confirm: <strong style={{ color:'var(--text-primary)' }}>{deleteTarget?.name}</strong>
            </label>
            <input
              className="form-input" autoFocus
              placeholder={deleteTarget?.name}
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmDelete(); }}
              style={{ borderColor: deleteConfirm && deleteConfirm.toLowerCase() !== (deleteTarget?.name || '').toLowerCase() ? 'var(--status-red)' : undefined }}
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary btn-sm" onClick={() => { setDeleteTarget(null); setDeleteConfirm(''); }}>Cancel</button>
          <button
            className="btn btn-sm"
            disabled={deleting || deleteConfirm.trim().toLowerCase() !== (deleteTarget?.name || '').toLowerCase()}
            onClick={confirmDelete}
            style={{ background:'var(--status-red)', color:'#fff', border:'none' }}
          >
            {deleting ? 'Deleting…' : 'Delete Permanently'}
          </button>
        </div>
      </Modal>

      {/* ── Modal ── */}
      <Modal isOpen={open} onClose={() => setOpen(false)} title={editing ? 'Edit Provider' : 'Add Provider'} wide>
        <form onSubmit={onSave}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Provider Name *</label>
              <input className="form-input" value={form.name} onChange={(e) => setForm(p => ({...p, name: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Type</label>
              <select className="form-select" value={form.provider_type} onChange={(e) => setForm(p => ({ ...p, provider_type: e.target.value, services: [] }))}>
                <option value="">Select type...</option>
                {providerTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Specialty</label>
              {specialties.length > 0 ? (
                <select
                  className="form-select"
                  value={form.specialty}
                  onChange={(e) => setForm(p => ({ ...p, specialty: e.target.value }))}
                >
                  <option value="">— Select specialty —</option>
                  {specialties.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input
                  className="form-input"
                  value={form.specialty}
                  onChange={(e) => setForm(p => ({ ...p, specialty: e.target.value }))}
                  placeholder="e.g., Orthopedics (add options in Settings → Custom Lists)"
                />
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" value={form.email} onChange={(e) => setForm(p => ({...p, email: e.target.value}))} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={(e) => setForm(p => ({...p, phone: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Fax</label>
              <input className="form-input" value={form.fax} onChange={(e) => setForm(p => ({...p, fax: e.target.value}))} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <AddressAutocomplete
              value={form.address}
              onChange={(v) => setForm(p => ({ ...p, address: v }))}
              onPlaceSelect={({ street, city, state, zip }) => setForm(p => ({
                ...p,
                address: street,
                city:    city  || p.city,
                state:   state || p.state,
                zip:     zip   || p.zip,
              }))}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" value={form.city} onChange={(e) => setForm(p => ({...p, city: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">State</label>
              <input className="form-input" value={form.state} onChange={(e) => setForm(p => ({...p, state: e.target.value}))} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">ZIP</label>
              <input className="form-input" value={form.zip} onChange={(e) => setForm(p => ({...p, zip: e.target.value}))} />
            </div>
            {/* Active toggle in modal */}
            <div className="form-group" style={{ display:'flex', flexDirection:'column', justifyContent:'flex-end', paddingBottom: 4 }}>
              <label className="form-label">Status</label>
              <div style={{ display:'flex', alignItems:'center', gap: 10, marginTop: 2 }}>
                <Toggle checked={form.is_active} onChange={(val) => setForm(p => ({...p, is_active: val}))} />
                <span style={{
                  fontSize: '0.88rem', fontWeight: 600,
                  color: form.is_active ? '#22c55e' : 'var(--text-muted)',
                }}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
          </div>
          {/* Services Offered — filtered by selected provider type */}
          <div className="form-group">
            <label className="form-label">Services Offered</label>
            {(() => {
              const typeServices = (form.provider_type && providerServices[form.provider_type]) || [];
              const selected = Array.isArray(form.services) ? form.services : [];

              if (!form.provider_type) {
                return <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', margin:'4px 0 0' }}>Select a Provider Type above to see available services.</p>;
              }
              if (typeServices.length === 0) {
                return <p style={{ fontSize:'0.8rem', color:'var(--text-muted)', margin:'4px 0 0' }}>No services defined for <strong>{form.provider_type}</strong> yet. Add them in Settings → Custom Lists → Services Offered by Provider Type.</p>;
              }

              const toggle = (svc) => {
                const next = selected.includes(svc)
                  ? selected.filter(s => s !== svc)
                  : [...selected, svc];
                setForm(p => ({ ...p, services: next }));
              };

              return (
                <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', padding:'8px', border:'1px solid var(--zeus-border)', borderRadius:'var(--radius-sm)', background:'var(--zeus-navy-light)' }}>
                  {typeServices.map(svc => {
                    const on = selected.includes(svc);
                    return (
                      <button
                        key={svc}
                        type="button"
                        onClick={() => toggle(svc)}
                        style={{
                          padding:'4px 12px', fontSize:'0.8rem', fontWeight: on ? 600 : 400,
                          border: `1px solid ${on ? 'var(--zeus-gold)' : 'var(--zeus-border)'}`,
                          borderRadius:'20px', cursor:'pointer',
                          background: on ? 'rgba(200,168,75,0.15)' : 'transparent',
                          color: on ? 'var(--zeus-gold)' : 'var(--text-secondary)',
                          transition:'all 0.12s',
                        }}
                      >
                        {on && ' '}{svc}
                      </button>
                    );
                  })}
                </div>
              );
            })()}
            {Array.isArray(form.services) && form.services.length > 0 && (
              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'4px' }}>
                {form.services.length} service{form.services.length !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" value={form.notes} onChange={(e) => setForm(p => ({...p, notes: e.target.value}))} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          </div>
        </form>
      </Modal>
    </>
  );
}
